import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    FooterCopyrightText,
    LinkWrapper,
    MobileAppVersion,
    EmptyLinkWrapper,
    FooterCopyrightWrapper,
} from './FooterCopyright.style';
import { FooterCopyrightState } from 'src/domains/layouts/webview/modules/layoutSkeleton/footer/copyright/FooterCopyright.state';
import { LanguageTokenType } from 'src/domains/layouts/state/languagesState/LanguagesUtils';
import { useCommon } from 'src/domains/common/Common';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

const TermsAndConditions = observer('TermsAndConditions', () => {
    const common = useCommon();
    const languagesState = LanguagesState.get(common);
    const configComponents = ConfigComponents.get(common);

    if (configComponents.config.hasFooterCopyrightTCLink) {
        return languagesState.translateTokens(
            languagesState.getTranslation(
                'footer.copyright.general-terms-and-conditions',
                'In order to register for this website, the user is required to accept the [generalTermsAndConditions]. In the event the General Terms and Conditions are updated, existing users may choose to discontinue using the products and services before the said update shall become effective, which is a minimum of two weeks after it has been announced.'
            ),

            (singleParam: LanguageTokenType) => {
                if (singleParam.tag === 'generalTermsAndConditions') {
                    return (
                        <LinkWrapper
                            to={{ account: 'static', static: 'terms-and-conditions' }}
                            dataTest='terms-and-conditions'
                        >
                            General Terms and Conditions
                        </LinkWrapper>
                    );
                }
            }
        );
    } else {
        return null;
    }
});
const CookiePolicy = observer('CookiePolicy', () => {
    const common = useCommon();
    const languagesState = LanguagesState.get(common);

    return languagesState.translateTokens(
        languagesState.getTranslation(
            'footer.copyright.cookie-policy',
            'This site uses cookies, for more information please see our [cookiePolicy].'
        ),

        (singleParam: LanguageTokenType) => {
            if (singleParam.tag === 'cookiePolicy') {
                return (
                    <LinkWrapper
                        to={{ account: 'static', static: 'cookie-policy' }}
                        dataTest='cookie-policy'
                    >
                        cookie policy
                    </LinkWrapper>
                );
            }
        }
    );
});

export const FooterCopyrightDefault = observer('FooterCopyrightDefault', () => {
    const common = useCommon();
    const state = FooterCopyrightState.get(common);

    const licenceNumber = (): JSX.Element | string => {
        if (state.licenceUrl === null || state.licenceUrl === '') {
            return <EmptyLinkWrapper data-test='licence-number-link'>{state.licenceNumber}</EmptyLinkWrapper>;
        }

        return (
            <LinkWrapper
                to={state.licenceUrl}
                dataTest='licence-number'
            >
                {state.licenceNumber}
            </LinkWrapper>
        );
    };

    const copyrightText = state.copyrightDescription;

    if (Array.isArray(copyrightText)) {
        const copyrightFirstPart = state.copyrightDescription[0] ?? '';
        const copyrightLastPart = state.copyrightDescription[1] ?? '';

        return (
            <>
                <TermsAndConditions /> {copyrightFirstPart}
                {licenceNumber()}
                {copyrightLastPart} <CookiePolicy />
            </>
        );
    }

    return (
        <>
            <TermsAndConditions /> {copyrightText} <CookiePolicy />
        </>
    );
});

export const FooterCopyright = observer('FooterCopyright', () => {
    const common = useCommon();
    const env = EnvironmentState.get(common);
    const { mobileWrapperType, mobileWrapperVersion } = env.envVariables;

    const version = mobileWrapperVersion ?? null;

    return (
        <>
            <FooterCopyrightWrapper>
                <FooterCopyrightText data-test='FooterCopyright'>
                    <FooterCopyrightDefault />
                </FooterCopyrightText>
            </FooterCopyrightWrapper>

            {mobileWrapperType === null || version === null ? null : (
                <MobileAppVersion>Mobile app version: {mobileWrapperVersion}</MobileAppVersion>
            )}
        </>
    );
});
